/**
 * Display names for study parts and cohorts (trial schema + dynamic tables).
 * Stored on study_overview.study_parts_part_names and study_overview.study_parts_cohort_names.
 */

import {
  buildRegimenArmDescriptor,
  formatRegimenColumnHeader,
  MAIN_STUDY_LABEL,
  parseDeclaredStudyPartCount,
  isMultiPartTrial,
  effectiveStudyPartSlotCount,
  hasConfiguredTrialPartCount,
} from './trialSchemaConfiguration';

export {
  MAIN_STUDY_LABEL,
  parseDeclaredStudyPartCount,
  isMultiPartTrial,
  effectiveStudyPartSlotCount,
  hasConfiguredTrialPartCount,
};

/** Cohort slot keys under study_parts[partN] — not regimen columns (those live on treatment_table). */
export const isStudyPartCohortKey = (key) => {
  const k = String(key || '');
  return k === 'direct' || /^cohort\d+$/i.test(k);
};

export const isRegimenKey = (key) => /^regimen\d+$/i.test(String(key || ''));

export const getStudyPartDisplayName = (partKey, partNames = {}, declaredPartCount = null) => {
  const custom = partNames?.[partKey];
  if (custom != null && String(custom).trim() !== '') {
    return String(custom).trim();
  }
  if (
    declaredPartCount !== null &&
    declaredPartCount !== undefined &&
    !isMultiPartTrial(declaredPartCount)
  ) {
    return MAIN_STUDY_LABEL;
  }
  const num = String(partKey || '').replace(/^part/i, '');
  return num ? `Part ${num}` : partKey;
};

export const getCohortDisplayName = (cohortKey, partKey, cohortNames = {}) => {
  if (cohortKey === 'direct') return 'Direct';
  const custom = cohortNames?.[partKey]?.[cohortKey];
  if (custom != null && String(custom).trim() !== '') {
    return String(custom).trim();
  }
  const num = String(cohortKey || '').replace(/^cohort/i, '');
  return num ? `Cohort ${num}` : cohortKey;
};

/** Build column descriptors for Study Parts / Statistical / Assessments tables. */
export const buildStudyPartCohortColumns = (
  studyParts,
  partNames = {},
  cohortNames = {},
  declaredPartCount = null
) => {
  const parts = Object.keys(studyParts || {}).sort();
  const allCohorts = [];

  parts.forEach((partKey) => {
    const partDisplayName = getStudyPartDisplayName(partKey, partNames, declaredPartCount);
    const cohortKeys = Object.keys(studyParts[partKey] || {})
      .filter(isStudyPartCohortKey)
      .sort();
    cohortKeys.forEach((cohortKey) => {
      allCohorts.push({
        partKey,
        cohortKey,
        partName: partDisplayName,
        cohortName: getCohortDisplayName(cohortKey, partKey, cohortNames),
      });
    });
  });

  const partGroups = {};
  parts.forEach((partKey) => {
    partGroups[partKey] = [];
  });
  allCohorts.forEach((cohort) => {
    if (!partGroups[cohort.partKey]) partGroups[cohort.partKey] = [];
    partGroups[cohort.partKey].push(cohort);
  });

  return { parts, allCohorts, partGroups };
};

/** One column per study part — for assessments and statistical considerations (not regimens). */
export const buildStudyPartOnlyColumns = (studyParts, partNames = {}, declaredPartCount = null) => {
  const parts = Object.keys(studyParts || {})
    .filter((k) => /^part\d+$/i.test(k))
    .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10));

  return parts.map((partKey) => ({
    partKey,
    storageKey: 'direct',
    partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
  }));
};

/** Evenly distribute regimens across study parts when counts change. */
export const distributeRegimensAcrossParts = (numParts, numRegimens, existing = {}) => {
  const nParts = parseInt(numParts) || 0;
  const nReg = parseInt(numRegimens) || 0;
  if (nParts <= 0 || nReg <= 0) return {};

  let sum = 0;
  for (let i = 1; i <= nParts; i++) {
    sum += parseInt(existing[`part${i}`]) || 0;
  }
  if (sum === nReg) {
    const preserved = {};
    for (let i = 1; i <= nParts; i++) {
      preserved[`part${i}`] = parseInt(existing[`part${i}`]) || 0;
    }
    return preserved;
  }

  const out = {};
  const base = Math.floor(nReg / nParts);
  let extra = nReg % nParts;
  for (let i = 1; i <= nParts; i++) {
    out[`part${i}`] = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
  }
  return out;
};

/**
 * Treatment table columns: study part → cohort (when multi-cohort) → regimen.
 * Regimen labels use agent name(s) + dose levels from Trial Schema B.
 */
export const buildTreatmentTableColumns = ({
  trialSchemaConfig,
  regimensPerPart,
  numRegimens,
  partNames = {},
  cohortNames = {},
  regimenArms = {},
  declaredPartCount = null,
}) => {
  const cfgParts = trialSchemaConfig?.parts || {};
  const nReg = parseInt(numRegimens) || 0;
  const partKeys = Object.keys(regimensPerPart || {})
    .filter((k) => /^part\d+$/i.test(k))
    .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10));

  const allRegimens = [];
  const partGroups = {};
  partKeys.forEach((k) => {
    partGroups[k] = [];
  });

  let globalIndex = 0;
  partKeys.forEach((partKey) => {
    const part = cfgParts[partKey] || {};
    const numCohorts = parseInt(part.num_cohorts) || 0;
    const count = parseInt(regimensPerPart[partKey]) || 0;

    if (numCohorts > 0) {
      const cohortRows = Array.isArray(part.cohort_rows) ? part.cohort_rows : [];
      for (let i = 0; i < count && globalIndex < nReg; i++) {
        globalIndex += 1;
        const cohortKey = `cohort${i + 1}`;
        const row = cohortRows[i] || {};
        const descriptor =
          regimenArms[globalIndex] ||
          regimenArms[String(globalIndex)] ||
          buildRegimenArmDescriptor(row, part);
        const entry = {
          partKey,
          cohortKey,
          cohortName: getCohortDisplayName(cohortKey, partKey, cohortNames),
          regimenKey: `regimen${globalIndex}`,
          globalIndex,
          partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
          descriptor,
          displayLabel: formatRegimenColumnHeader(globalIndex, descriptor),
        };
        allRegimens.push(entry);
        partGroups[partKey].push(entry);
      }
    } else {
      const directRows = Array.isArray(part.direct_rows) ? part.direct_rows : [];
      for (let r = 0; r < count && globalIndex < nReg; r++) {
        globalIndex += 1;
        const row = directRows[r] || {};
        const descriptor =
          regimenArms[globalIndex] ||
          regimenArms[String(globalIndex)] ||
          buildRegimenArmDescriptor(row, part);
        const entry = {
          partKey,
          cohortKey: 'direct',
          cohortName: null,
          regimenKey: `regimen${globalIndex}`,
          globalIndex,
          partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
          descriptor,
          displayLabel: formatRegimenColumnHeader(globalIndex, descriptor),
        };
        allRegimens.push(entry);
        partGroups[partKey].push(entry);
      }
    }
  });

  while (globalIndex < nReg) {
    globalIndex += 1;
    const regimenKey = `regimen${globalIndex}`;
    const partKey = partKeys[partKeys.length - 1] || 'part1';
    if (!partGroups[partKey]) partGroups[partKey] = [];
    const descriptor =
      regimenArms[globalIndex] || regimenArms[String(globalIndex)] || '';
    const entry = {
      partKey,
      cohortKey: 'direct',
      cohortName: null,
      regimenKey,
      globalIndex,
      partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
      descriptor,
      displayLabel: formatRegimenColumnHeader(globalIndex, descriptor),
    };
    allRegimens.push(entry);
    partGroups[partKey].push(entry);
  }

  return { parts: partKeys, allRegimens, partGroups };
};

/**
 * Map flat regimen1..N keys to study parts for treatment table column grouping.
 */
export const buildRegimenPartColumns = (regimensPerPart, numRegimens, partNames = {}, declaredPartCount = null) => {
  const nReg = parseInt(numRegimens) || 0;
  const partKeys = Object.keys(regimensPerPart || {})
    .filter((k) => /^part\d+$/i.test(k))
    .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10));

  const allRegimens = [];
  const partGroups = {};
  partKeys.forEach((k) => {
    partGroups[k] = [];
  });

  let globalIndex = 0;
  partKeys.forEach((partKey) => {
    const count = parseInt(regimensPerPart[partKey]) || 0;
    for (let r = 0; r < count && globalIndex < nReg; r++) {
      globalIndex += 1;
      const regimenKey = `regimen${globalIndex}`;
      const entry = {
        partKey,
        regimenKey,
        globalIndex,
        partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
      };
      allRegimens.push(entry);
      partGroups[partKey].push(entry);
    }
  });

  while (globalIndex < nReg) {
    globalIndex += 1;
    const regimenKey = `regimen${globalIndex}`;
    const partKey = partKeys[partKeys.length - 1] || 'part1';
    if (!partGroups[partKey]) partGroups[partKey] = [];
    const entry = {
      partKey,
      regimenKey,
      globalIndex,
      partName: getStudyPartDisplayName(partKey, partNames, declaredPartCount),
    };
    allRegimens.push(entry);
    partGroups[partKey].push(entry);
  }

  return { parts: partKeys, allRegimens, partGroups };
};
